import { isTravelHomeSectionHidden } from "../../../components/travelTextTokens";
import React from "react";
import Link from "next/link";
import { sharedStyles, resolveImage } from "./_shared";
import { ASSET_URL } from "../../../config";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import Button from "../components/Button";
import { isExternalUrl, buildLinkHref, buildMenuUrl } from "../../../components/normalizeMenuUrl";

const bannersStyles = `
.tr-banners-wrap {
  padding: clamp(12px, 2vw, 24px) clamp(16px, 4vw, 32px);
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: clamp(16px, 2.5vw, 28px);
}
.tr-banner {
  position: relative;
  border-radius: 22px;
  overflow: hidden;
  isolation: isolate;
  min-height: 260px;
  display: flex;
  align-items: flex-end;
  box-shadow: 0 8px 32px rgba(0,0,0,.12), 0 2px 8px rgba(0,0,0,.06);
}
.tr-banner__img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  z-index: 0;
}
.tr-banner__wash {
  position: absolute; inset: 0; z-index: 1;
  background: linear-gradient(92deg, rgba(9,18,26,.78) 0%, rgba(9,18,26,.45) 55%, rgba(9,18,26,.15) 100%);
}
[dir="rtl"] .tr-banner__wash {
  background: linear-gradient(-92deg, rgba(9,18,26,.78) 0%, rgba(9,18,26,.45) 55%, rgba(9,18,26,.15) 100%);
}
.tr-banner__content {
  position: relative; z-index: 2;
  padding: clamp(22px, 4vw, 44px);
  color: white;
  max-width: 640px;
}
.tr-banner__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: rgba(255,255,255,.92);
  background: rgba(255,255,255,.14);
  border: 1px solid rgba(255,255,255,.22);
  padding: 6px 12px;
  border-radius: 999px;
  margin-bottom: 14px;
  backdrop-filter: blur(6px);
}
.tr-banner__title {
  margin: 0 0 6px;
  font-size: clamp(22px, 3.2vw, 34px);
  font-weight: 700;
  letter-spacing: -.01em;
  line-height: 1.1;
  text-wrap: balance;
}
.tr-banner__subtitle {
  margin: 0 0 10px;
  color: rgba(255,255,255,.92);
  font-size: 17px;
  font-weight: 500;
}
.tr-banner__desc {
  margin: 0 0 18px;
  color: rgba(255,255,255,.84);
  font-size: 15px;
  line-height: 1.5;
  max-width: 520px;
}
.tr-banner__ctas {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
`;

// Rendu d'un bouton (interne ou externe) avec fallback propre.
function BannerCta({ org, label, url, icon, variant }) {
    if (!label) return null;

    const iconNode = icon ? renderIcon(icon, 20) : null;

    if (!url) {
        return (
            <Button variant={variant} size="md" as="button">
                {iconNode}
                {label}
            </Button>
        );
    }
    const isExternal = isExternalUrl(url, org);
    if (isExternal) {
        return (
            <Button variant={variant} size="md" as="a" href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                {iconNode}
                {label}
            </Button>
        );
    }
    return (
        <Link href={buildLinkHref(buildMenuUrl(url, org), org)}>
            <Button variant={variant} size="md" as="span">
                {iconNode}
                {label}
            </Button>
        </Link>
    );
}

// Banners : empile toutes les bannières configurées via travel.homepage.banners.
// Structure de slide (même que hero) : image, eyebrow, eyebrow_icon, title,
// subtitle, description, btn1_label/url/icon, btn2_label/url/icon. Autonome.
export default function Banners({ context }) {
    const { org, travel, lang } = context;

    const banners = (travel?.homepage?.banners || [])
        .filter(b => b?.image)
        .sort((a, b) => (a.position || 0) - (b.position || 0));

    if (banners.length === 0 || isTravelHomeSectionHidden(context, "home_banners")) return null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + bannersStyles }} />
            <div className="tr-banners-wrap">
                {banners.map((banner) => {
                    const localized = applyLang(banner, lang);
                    const imgSrc = resolveImage(localized.image, ASSET_URL);

                    return (
                        <section key={banner.id} className="tr-banner">
                            <img src={imgSrc} alt="" loading="lazy" className="tr-banner__img" />
                            <div className="tr-banner__wash" />
                            <div className="tr-banner__content">
                                {localized.eyebrow && (
                                    <span className="tr-banner__eyebrow">
                                        {banner.eyebrow_icon && renderIcon(banner.eyebrow_icon, 14)}
                                        {localized.eyebrow}
                                    </span>
                                )}
                                {localized.title && (
                                    <h3 className="tr-banner__title">{localized.title}</h3>
                                )}
                                {localized.subtitle && (
                                    <p className="tr-banner__subtitle">{localized.subtitle}</p>
                                )}
                                {localized.description && (
                                    <p className="tr-banner__desc">{localized.description}</p>
                                )}
                                {(localized.btn1_label || localized.btn2_label) && (
                                    <div className="tr-banner__ctas">
                                        <BannerCta
                                            org={org}
                                            label={localized.btn1_label}
                                            url={localized.btn1_url}
                                            icon={banner.btn1_icon}
                                            variant="primary"
                                        />
                                        <BannerCta
                                            org={org}
                                            label={localized.btn2_label}
                                            url={localized.btn2_url}
                                            icon={banner.btn2_icon}
                                            variant="secondary"
                                        />
                                    </div>
                                )}
                            </div>
                        </section>
                    );
                })}
            </div>
        </>
    );
}
